/*
* @author  Agence Dn'D <contact@dnd.fr>
* @license https://opensource.org/licenses/osl-3.0.php  Open Software License (OSL 3.0)
* @link    https://www.dnd.fr/
*/

(() => {

    const mobileBreakpoint = '(max-width: 999.98px)';
    const blockSelector = '.samples-block';
    const dragZoneSelector = '.samples-block__handle, .samples-block__heading';
    const swipeThreshold = 40;

    const isMobile = () => window.matchMedia(mobileBreakpoint).matches;

    const isOpen = (block) => {
        const trigger = block.querySelector('[data-toggle-target]');
        return trigger && trigger.getAttribute('aria-expanded') === 'true';
    };

    const toggleOpen = (block) => {
        const trigger = block.querySelector('[data-toggle-target]');
        if (trigger) trigger.click();
    };

    document.querySelectorAll(blockSelector).forEach((block) => {
        let startY = 0;
        let tracking = false;

        block.addEventListener('touchstart', (event) => {
            if (!isMobile() || !event.target.closest(dragZoneSelector)) return;
            startY = event.touches[0].clientY;
            tracking = true;
        }, { passive: true });

        block.addEventListener('touchmove', (event) => {
            if (!tracking || !isMobile()) return;
            if (Math.abs(event.touches[0].clientY - startY) > 10) event.preventDefault();
        }, { passive: false });

        block.addEventListener('touchend', (event) => {
            if (!tracking || !isMobile()) return;
            tracking = false;

            const deltaY = event.changedTouches[0].clientY - startY;
            if (deltaY < -swipeThreshold && !isOpen(block)) toggleOpen(block);
            else if (deltaY > swipeThreshold && isOpen(block)) toggleOpen(block);
        });
    });

    const cookieName = 'stonshop_samples_selection';
    const cookieDays = 7;
    const visibleSlotCount = 6;

    const getCookie = (name) => {
        const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
        return match ? decodeURIComponent(match[1]) : null;
    };

    const setCookie = (name, value, days) => {
        const expires = new Date(Date.now() + days * 864e5).toUTCString();
        document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
    };

    const deleteCookie = (name) => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    };

    const loadState = () => {
        try {
            const parsed = JSON.parse(getCookie(cookieName) || '[]');
            return Array.isArray(parsed) ? parsed : [];
        } catch (err) {
            return [];
        }
    };

    const saveState = (state) => {
        if (state.length === 0) deleteCookie(cookieName);
        else setCookie(cookieName, JSON.stringify(state), cookieDays);
    };

    const SamplesList = {
        state: loadState(),
        isExpanded: false,
        elements: {},

        init() {
            this.elements.block = document.querySelector('[data-samples-block]');
            if (!this.elements.block) return;

            this.elements.count = this.elements.block.querySelector('[data-samples-count]');
            this.elements.slots = this.elements.block.querySelector('[data-samples-slots]');
            this.elements.slotsExtra = this.elements.block.querySelector('[data-samples-slots-extra]');
            this.elements.moreBtn = this.elements.block.querySelector('[data-samples-more]');
            this.elements.moreLabel = this.elements.block.querySelector('[data-samples-more-label]');
            this.elements.submitBtn = this.elements.block.querySelector('[data-samples-add-to-cart]');
            this.elements.toggleTrigger = this.elements.block.querySelector('[data-toggle-target]');
            this.elements.slotTemplate = document.getElementById('SampleSlotTemplate');

            this.render();
            this.syncPageButtons();
            this.openOnDesktopIfNeeded();
            this.watchBodyScrollLock();

            document.addEventListener('click', (event) => {
                const addBtn = event.target.closest('[data-quick-add-to-cart-btn]');
                if (addBtn) this.handleAdd(addBtn);
            });

            this.elements.block.addEventListener('click', (event) => {
                const removeBtn = event.target.closest('[data-sample-remove]');
                if (removeBtn) this.handleRemove(removeBtn.dataset.variantId);
            });

            this.elements.moreBtn?.addEventListener('click', () => {
                this.isExpanded = !this.isExpanded;
                this.render();
            });

            this.elements.submitBtn?.addEventListener('click', () => this.handleSubmit());

            const collectionListing = document.querySelector('.filters-adjacent.collection-listing, .collection-listing');
            if (collectionListing && 'MutationObserver' in window) {
                new MutationObserver(() => this.syncPageButtons())
                    .observe(collectionListing, { childList: true });
            }
        },

        watchBodyScrollLock() {
            const trigger = document.querySelector('[data-toggle-target="#SamplesBlockContent"]');
            if (!trigger) return;

            const bodyLockClass = 'samples-block-scroll-lock';

            const syncBodyClass = () => {
                const expanded = trigger.getAttribute('aria-expanded') === 'true';
                document.body.classList.toggle(bodyLockClass, expanded);
            };

            syncBodyClass();

            new MutationObserver(syncBodyClass)
                .observe(trigger, { attributes: true, attributeFilter: ['aria-expanded'] });
        },

        openOnDesktopIfNeeded() {
            if (!this.elements.toggleTrigger || isMobile()) return;

            const content = document.querySelector(this.elements.toggleTrigger.dataset.toggleTarget);
            if (content?.classList.contains('toggle-target--hidden')) {
                this.elements.toggleTrigger.click();
            }
        },

        handleAdd(button) {
            const container = button.closest('[data-sample-product]');
            if (!container) return;

            const { variantId } = container.dataset;
            if (this.state.some((item) => item.variantId === variantId)) return;

            this.state.push({
                variantId,
                productId: container.dataset.productId,
                title: container.dataset.productTitle,
                image: container.dataset.productImage,
                url: container.dataset.productUrl
            });

            saveState(this.state);
            this.render();
            this.syncPageButtons();
        },

        handleRemove(variantId) {
            this.state = this.state.filter((item) => item.variantId !== variantId);
            saveState(this.state);
            this.render();
            this.syncPageButtons();
        },

        buildSlotContent(item) {
            const fragment = this.elements.slotTemplate.content.cloneNode(true);
            const media = fragment.querySelector('.samples-block__slot-media');
            const img = fragment.querySelector('img');

            if (item.image) {
                img.src = item.image;
                img.alt = item.title || '';
            } else {
                media.hidden = true;
            }

            fragment.querySelector('.samples-block__slot-title').textContent = item.title || '';

            const removeBtn = fragment.querySelector('[data-sample-remove]');
            removeBtn.dataset.variantId = item.variantId;
            removeBtn.setAttribute('aria-label', `Supprimer ${item.title || ''}`);

            return fragment;
        },

        populateSlot(slot, item) {
            const oldSlot = slot.querySelector('.samples-block__slot-media');
            if (oldSlot) oldSlot.remove();

            const removeBtn = slot.querySelector('[data-sample-remove]');
            if (removeBtn) removeBtn.remove();

            slot.classList.toggle('samples-block__slot--filled', !!item);

            if (item) {
                slot.dataset.variantId = item.variantId;
                slot.appendChild(this.buildSlotContent(item));
            } else {
                delete slot.dataset.variantId;
            }
        },

        render() {
            if (!this.elements.block) return;

            if (this.elements.count) this.elements.count.textContent = this.state.length;
            if (this.elements.submitBtn) this.elements.submitBtn.disabled = this.state.length === 0;

            this.elements.slots
                ?.querySelectorAll('[data-sample-slot]')
                .forEach((slot, index) => this.populateSlot(slot, this.state[index]));

            const extraItems = this.state.slice(visibleSlotCount);

            if (this.elements.slotsExtra) {
                this.elements.slotsExtra.innerHTML = '';

                extraItems.forEach((item) => {
                    const slot = document.createElement('div');
                    slot.className = 'samples-block__slot samples-block__slot--extra';
                    slot.dataset.sampleSlot = '';
                    this.populateSlot(slot, item);
                    this.elements.slotsExtra.appendChild(slot);
                });

                this.elements.slotsExtra.hidden = !(extraItems.length && this.isExpanded);
            }

            if (this.elements.moreBtn) {
                this.elements.moreBtn.hidden = extraItems.length === 0;
                if (this.elements.moreLabel) {
                    this.elements.moreLabel.textContent = this.isExpanded ? 'Voir moins' : 'Voir plus';
                }
            }
        },

        syncPageButtons() {
            document.querySelectorAll('[data-sample-product]').forEach((container) => {
                const inList = this.state.some((item) => item.variantId === container.dataset.variantId);
                container.classList.toggle('is-in-list', inList);
            });
        },

        async handleSubmit() {
            if (this.state.length === 0 || this.elements.submitBtn.disabled) return;

            this.elements.submitBtn.disabled = true;
            this.elements.submitBtn.classList.add('is-loading');

            const items = this.state.map((item) => ({ id: item.variantId, quantity: 1 }));

            try {
                const cartAddUrl = window.theme?.routes?.cartAdd || '/cart/add.js';

                const response = await fetch(cartAddUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ items })
                });

                if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

                this.state = [];
                this.isExpanded = false;
                deleteCookie(cookieName);
                this.render();
                this.syncPageButtons();

                document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
                document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
                document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
                    detail: { opener: this.elements.submitBtn }
                }));
            } catch (error) {
                console.warn(error);
            } finally {
                this.elements.submitBtn.classList.remove('is-loading');
                this.elements.submitBtn.disabled = this.state.length === 0;
            }
        }
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => SamplesList.init());
    } else {
        SamplesList.init();
    }
})();